import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, LineChart, Bot, LogOut, Menu, X, Target, Swords, MoreHorizontal,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import Seo from "@/components/Seo";

// Primary nav: exactly 5 entries. Everything else is grouped on /more.
const NAV = [
  { to: "/dashboard", label: "Today", icon: LayoutDashboard, testid: "nav-today" },
  { to: "/training", label: "Path", icon: Swords, testid: "nav-path" },
  { to: "/replay", label: "Practice", icon: LineChart, testid: "nav-practice" },
  { to: "/coach", label: "Coach", icon: Bot, testid: "nav-coach" },
  { to: "/more", label: "More", icon: MoreHorizontal, testid: "nav-more" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [prog, setProg] = useState(null);


  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setProg({ ...r.data.progress, display_title: r.data.display_title })).catch(() => {});
    load();
    window.addEventListener("tc:progress", load);
    return () => window.removeEventListener("tc:progress", load);
  }, []);

  const doLogout = () => { logout(); navigate("/login"); };

  const NavItem = ({ n }) => (
    <NavLink
      to={n.to}
      data-testid={n.testid}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-tc-coach/15 text-tc-coach border border-tc-coach/30"
            : "text-tc-sub hover:text-tc-txt hover:bg-white/5 border border-transparent"
        }`
      }
    >
      <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
      {n.label}
    </NavLink>
  );

  const NavList = () => (
    <nav className="flex flex-col gap-1 mt-2 flex-1 min-h-0 overflow-y-auto" data-testid="nav-scroll">
      {NAV.map((n) => <NavItem key={n.to} n={n} />)}
    </nav>
  );

  const Brand = () => (
    <div className="flex items-center gap-2.5 px-2">
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-tc-coach to-tc-mastery flex items-center justify-center shadow-lg shadow-tc-coach/20">
        <Target className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="font-heading font-extrabold tracking-tight text-tc-txt leading-none">TRADECRAFT</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mt-0.5">Master the craft</div>
      </div>
    </div>
  );

  return (
    <div className="dark min-h-screen bg-tc-bg text-tc-txt flex w-full max-w-full overflow-x-clip">
      {/* Authenticated app is always private — never indexable. */}
      <Seo title="TRADECRAFT" path={location.pathname} noindex={true} />
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/10 bg-[#0A0E16] py-5 px-3 sticky top-0 h-screen">
        <Brand />
        <NavList />
        <div className="shrink-0 pt-2 mt-2 border-t border-white/10 px-2 space-y-2">
          {prog && (
            <div className="tc-card p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-tc-xp flex flex-wrap gap-x-1.5">
                <span data-testid="sidebar-rank">{prog.rank}</span>
                {prog.display_title && <span className="text-tc-coach" data-testid="sidebar-title">· {prog.display_title}</span>}
              </div>
              <div className="text-sm font-semibold mt-0.5">Level {prog.level}</div>
              <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-tc-xp rounded-full transition-all"
                  style={{ width: `${Math.min(100, ((prog.xp_into_level) / prog.xp_per_level) * 100)}%` }} />
              </div>
              <div className="text-[11px] font-mono text-tc-muted mt-1">
                {prog.total_xp} / {prog.xp_for_next} XP
              </div>
            </div>
          )}
          <button data-testid="logout-btn" onClick={doLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-tc-sub hover:text-tc-bear hover:bg-tc-bear/10 transition-colors">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 tc-glass border-b border-white/10 flex items-center justify-between px-4">
        <Brand />
        <div className="flex items-center gap-1">
          <NotificationBell variant="mobile" />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Desktop top-right bell */}
      <div className="hidden lg:block fixed top-4 right-6 z-40"><NotificationBell variant="desktop" /></div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative w-72 max-w-[85vw] h-full bg-[#0A0E16] border-r border-white/10 py-5 px-3 flex flex-col">
            <div className="flex items-center justify-between">
              <Brand />
              <button onClick={() => setOpen(false)} className="p-2"><X className="w-5 h-5" /></button>
            </div>
            <NavList />
            <div className="shrink-0 pt-2 mt-2 border-t border-white/10">
              <button data-testid="logout-btn-mobile" onClick={doLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-tc-sub hover:text-tc-bear">
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 max-w-full overflow-x-clip pt-14 lg:pt-0">{children}</main>
    </div>
  );
}
